import { Box } from "@mui/material";
import { IconChevronLeft } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { Members } from "./parts";

const InviteMembers = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const location = useLocation();
  const { gameId, groupId, playersInGame } = location.state || {};

  const membersObserverRef = useRef(null);

  const [selectedMembers, setSelectedMembers] = useState([]);
  const [addingPlayer, setAddingPlayer] = useState(false);

  // Members state
  const [members, setMembers] = useState([]);
  const [membersPagination, setMembersPagination] = useState({
    page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersLoadingMore, setMembersLoadingMore] = useState(false);

  const handleMemberSelect = (member) => {
    setSelectedMembers((prev) =>
      prev.map((m) => m.id).includes(member.id)
        ? prev.filter(({ id }) => id !== member.id)
        : [...prev, member],
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    setAddingPlayer(true);
    const payload = {
      user_ids: selectedMembers.map((member) => member.id),
    };
    try {
      const response = await apiService.invitePlayersToGame(gameId, payload);
      if (response.success) {
        showToast(response.message, "success");
        navigate(`/game-details/${gameId}`);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setAddingPlayer(false);
    }
  };

  // Fetch members
  const fetchMembers = async (params, append = false) => {
    try {
      if (append) {
        setMembersLoadingMore(true);
      } else {
        setMembersLoading(true);
      }

      const response = await apiService.fetchGroupMembers(groupId, params);

      if (response.success) {
        if (append) {
          setMembers((prev) => [
            ...prev,
            ...response.data.filter(
              (member) => !playersInGame.includes(member.id),
            ),
          ]);
        } else {
          setMembers(
            // response.data,
            response.data.filter(
              (member) => !playersInGame.includes(member.id),
            ),
          );
        }
        setMembersPagination(response.pagination);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      if (append) {
        setMembersLoadingMore(false);
      } else {
        setMembersLoading(false);
      }
    }
  };

  // Load more members
  const loadMoreMembers = () => {
    if (!membersLoadingMore && membersPagination.has_next_page) {
      const nextPage = membersPagination.page + 1;
      fetchMembers({ page: nextPage, limit: membersPagination.limit }, true);
    }
  };

  // Initial load when tab changes
  useEffect(() => {
    if (members.length === 0) {
      fetchMembers({
        page: 1,
        limit: 20,
      });
    }
  }, []);

  // Intersection Observer for members
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          membersPagination.has_next_page &&
          !membersLoadingMore
        ) {
          loadMoreMembers();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (membersObserverRef.current) {
      observer.observe(membersObserverRef.current);
    }

    return () => {
      if (membersObserverRef.current) {
        observer.unobserve(membersObserverRef.current);
      }
    };
  }, [membersPagination.has_next_page, membersLoadingMore, loadMoreMembers]);

  return (
    <Box
      sx={{
        mx: "auto",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Responsive Back Button and Header */}
        <Box sx={{ mb: 3, display: { xs: "block", md: "none" } }}>
          <Button variant="back" onClick={handleBack} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Box
            onClick={handleBack}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              cursor: "pointer",
              color: "#1890FF",
              "&:hover": {
                color: "#40A9FF",
              },
            }}
          >
            <IconChevronLeft size={20} />
          </Box>
          <Text variant="pageHeader">Invite Members</Text>
        </Box>
        <Text component="p" variant="paragraph" sx={{ mb: 3 }}>
          Select members to invite. We’ll send them a message to confirm and pay
          for their spot.
        </Text>

        {/* Members List Component */}
        <Box>
          <Members
            selectedMembers={selectedMembers}
            onMemberSelect={handleMemberSelect}
            members={members}
            pagination={membersPagination}
            loading={membersLoading}
            loadingMore={membersLoadingMore}
            observerRef={membersObserverRef}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          pt: { xs: 0, md: 2 },
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: { md: "0 2px 8px rgba(0,0,0,0.1)" },
          width: { xs: "auto", md: "250px", lg: "256px" },
          position: { md: "sticky" },
          top: { xs: "auto", md: 0 },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
          height: "fit-content",
        }}
      >
        <Box>
          {selectedMembers.map((member, index) => (
            <Text key={index} sx={{ color: "#666666" }}>
              {member.first_name} {member.last_name}
              {index < selectedMembers.length - 1 ? ", " : ""}
            </Text>
          ))}
        </Box>
        <Button
          variant="primary"
          onClick={() => {
            handleSubmit();
          }}
          disabled={selectedMembers.length === 0 || addingPlayer}
          sx={{
            width: "100%",
            py: 1.5,
            fontSize: "16px",
          }}
        >
          {addingPlayer ? "Sending invite..." : "Send invite"}
        </Button>
      </Box>
    </Box>
  );
};

export default InviteMembers;
